/*
 * Copyright (c) 2024. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import { GQL_PRESEED_GLOBAL } from '@amzn/prerender-contract'
import { getCacheManager } from '@/core/offlineCacheManager'

/**
 * Populate the CacheManager from the static-render preseed BEFORE the app makes
 * its first data fetch, so a preseeded query resolves through the NORMAL
 * cache-manager read in `gqlFetch` — there is no special-case branch in the
 * fetch hot path.
 *
 * The render host publishes the preseed on `globalThis[GQL_PRESEED_GLOBAL]`
 * (an inline script in the served prerendered page) BEFORE the app module
 * evaluates. Its shape is a `storageKey → cached response body` map captured
 * from the render's OWN cache manager (see `@amzn/static-prerender`'s render
 * worker): the render drives a stubbed in-memory cache manager, `gqlFetch`'s
 * normal cache-write fills it, and the render serializes those entries here.
 * The key is the CacheManager's full storage key, so this is a straight
 * write-back — neither side has to know the manager's key prefix.
 *
 * No-op in a normal browser session (the global is unset). Awaited at bootstrap.
 */
export async function preseedCacheManager(): Promise<void> {
    const seed = readSeed()
    if (!seed) return
    const cacheManager = getCacheManager()
    // A bare Response carries just the cached gql envelope body; `gqlFetch`'s
    // read supplies the freshness window (maxAge / stale-while-revalidate) via
    // its cache-control overrides, so the write-back needs no headers of its own.
    await Promise.all(
        Object.entries(seed).map(([storageKey, body]) =>
            cacheManager.put(storageKey, new Response(String(body)))
        )
    )
}

function readSeed(): Record<string, unknown> | undefined {
    if (typeof globalThis === 'undefined') return undefined
    const seed = (globalThis as unknown as Record<string, unknown>)[GQL_PRESEED_GLOBAL]
    return seed && typeof seed === 'object' ? (seed as Record<string, unknown>) : undefined
}
